import errorHandler from '../utils/errorHandler';

class PaymentService {
    constructor() {
        this.razorpayLoaded = false;
        this.stripeLoaded = false;
        this.currentProvider = null;
    }

    // Initialize payment providers
    async initializeProviders() {
        try {
            // Load Razorpay
            if (!this.razorpayLoaded) {
                await this.loadRazorpay();
            }

            // Load Stripe
            if (!this.stripeLoaded) {
                await this.loadStripe();
            }

            return true;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Payment initialization' });
            return false;
        }
    }

    // Load Razorpay SDK
    async loadRazorpay() {
        return new Promise((resolve, reject) => {
            if (window.Razorpay) {
                this.razorpayLoaded = true;
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => {
                this.razorpayLoaded = true;
                resolve();
            };
            script.onerror = () => {
                reject(new Error('Failed to load Razorpay SDK'));
            };
            document.head.appendChild(script);
        });
    }

    // Load Stripe SDK
    async loadStripe() {
        return new Promise((resolve, reject) => {
            if (window.Stripe) {
                this.stripeLoaded = true;
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.onload = () => {
                this.stripeLoaded = true;
                resolve();
            };
            script.onerror = () => {
                reject(new Error('Failed to load Stripe SDK'));
            };
            document.head.appendChild(script);
        });
    }

    // Get available payment methods
    getAvailablePaymentMethods() {
        const methods = [
            {
                id: 'razorpay',
                name: 'Razorpay',
                description: 'Pay with UPI, Cards, Wallets, Net Banking',
                icon: '💳',
                supportedMethods: ['upi', 'card', 'wallet', 'netbanking'],
                isAvailable: this.razorpayLoaded
            },
            {
                id: 'stripe',
                name: 'Stripe',
                description: 'Pay with Cards, Apple Pay, Google Pay',
                icon: '💎',
                supportedMethods: ['card', 'apple_pay', 'google_pay'],
                isAvailable: this.stripeLoaded
            },
            {
                id: 'cod',
                name: 'Cash on Delivery',
                description: 'Pay when service is delivered',
                icon: '💰',
                supportedMethods: ['cash'],
                isAvailable: true
            }
        ];

        return methods.filter(method => method.isAvailable);
    }

    // Process payment with Razorpay
    async processRazorpayPayment(paymentData) {
        try {
            if (!this.razorpayLoaded) {
                await this.loadRazorpay();
            }

            const options = {
                key: import.meta.env.VITE_RAZORPAY_KEY_ID,
                amount: paymentData.amount * 100, // Convert to paise
                currency: paymentData.currency || 'INR',
                name: paymentData.business_name || 'Spoon & Spice',
                description: paymentData.description || 'Catering Service Payment',
                order_id: paymentData.razorpay_order_id,
                prefill: {
                    name: paymentData.customer_name,
                    email: paymentData.customer_email,
                    contact: paymentData.customer_phone
                },
                theme: {
                    color: '#6366F1'
                },
                handler: (response) => {
                    return this.handleRazorpaySuccess(response, paymentData);
                },
                modal: {
                    ondismiss: () => {
                        return this.handlePaymentCancelled(paymentData);
                    }
                }
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();

            return new Promise((resolve, reject) => {
                this.razorpayResolve = resolve;
                this.razorpayReject = reject;
            });

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Razorpay payment' });
            throw error;
        }
    }

    // Process payment with Stripe
    async processStripePayment(paymentData) {
        try {
            if (!this.stripeLoaded) {
                await this.loadStripe();
            }

            const stripe = window.Stripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
            
            const { error, paymentIntent } = await stripe.confirmCardPayment(
                paymentData.stripe_payment_intent_client_secret,
                {
                    payment_method: {
                        card: paymentData.card_element,
                        billing_details: {
                            name: paymentData.customer_name,
                            email: paymentData.customer_email,
                            phone: paymentData.customer_phone
                        }
                    }
                }
            );

            if (error) {
                throw new Error(error.message);
            }

            return this.handleStripeSuccess(paymentIntent, paymentData);

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Stripe payment' });
            throw error;
        }
    }

    // Handle Razorpay payment success
    async handleRazorpaySuccess(response, paymentData) {
        try {
            // Verify payment on backend
            const verificationResponse = await fetch('/api/payments/razorpay/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_signature: response.razorpay_signature,
                    booking_id: paymentData.booking_id,
                    amount: paymentData.amount
                })
            });

            const result = await verificationResponse.json();

            if (result.success) {
                this.razorpayResolve?.(result);
                return result;
            } else {
                throw new Error(result.message || 'Payment verification failed');
            }

        } catch (error) {
            this.razorpayReject?.(error);
            throw error;
        }
    }

    // Handle Stripe payment success
    async handleStripeSuccess(paymentIntent, paymentData) {
        try {
            // Update booking with payment details
            const response = await fetch('/api/payments/stripe/confirm', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    payment_intent_id: paymentIntent.id,
                    booking_id: paymentData.booking_id,
                    amount: paymentData.amount
                })
            });

            const result = await response.json();

            if (result.success) {
                return result;
            } else {
                throw new Error(result.message || 'Payment confirmation failed');
            }

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Stripe payment confirmation' });
            throw error;
        }
    }

    // Handle payment cancellation
    handlePaymentCancelled(paymentData) {
        errorHandler.showWarning('Payment was cancelled');
        this.razorpayReject?.(new Error('Payment cancelled by user'));
    }

    // Create payment intent (for Stripe)
    async createPaymentIntent(amount, currency = 'INR', metadata = {}) {
        try {
            const response = await fetch('/api/payments/stripe/create-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    amount: amount * 100, // Convert to cents
                    currency: currency.toLowerCase(),
                    metadata
                })
            });

            const result = await response.json();

            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.message || 'Failed to create payment intent');
            }

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Create payment intent' });
            throw error;
        }
    }

    // Create Razorpay order
    async createRazorpayOrder(amount, currency = 'INR', receipt = null) {
        try {
            const response = await fetch('/api/payments/razorpay/create-order', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    amount: amount * 100, // Convert to paise
                    currency: currency.toLowerCase(),
                    receipt: receipt || `receipt_${Date.now()}`
                })
            });

            const result = await response.json();

            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.message || 'Failed to create Razorpay order');
            }

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Create Razorpay order' });
            throw error;
        }
    }

    // Get payment status
    async getPaymentStatus(bookingId) {
        try {
            const response = await fetch(`/api/payments/status/${bookingId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const result = await response.json();

            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.message || 'Failed to get payment status');
            }

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get payment status' });
            throw error;
        }
    }

    // Refund payment
    async refundPayment(paymentId, amount = null, reason = 'Customer request') {
        try {
            const response = await fetch('/api/payments/refund', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    payment_id: paymentId,
                    amount: amount,
                    reason: reason
                })
            });

            const result = await response.json();

            if (result.success) {
                errorHandler.showSuccess('Refund processed successfully');
                return result.data;
            } else {
                throw new Error(result.message || 'Refund failed');
            }

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Refund payment' });
            throw error;
        }
    }

    // Get payment history
    async getPaymentHistory(page = 1, limit = 10) {
        try {
            const response = await fetch(`/api/payments/history?page=${page}&limit=${limit}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const result = await response.json();

            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.message || 'Failed to get payment history');
            }

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get payment history' });
            throw error;
        }
    }
}

// Create singleton instance
const paymentService = new PaymentService();

export default paymentService;
